import { useI18N } from "@sundae/react-hooks";
import { Notice, Text, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, useMemo } from "react";

import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";

const MaybeTasteTestContributeWidgetOverlay: FC = () => {
  const { t } = useI18N("taste-test");
  const {
    derived: { hasContribution },
    state: {
      tasteTestStatus: {
        isTasteTestClosing,
        isTasteTestInactive,
        isTasteTestConcluded,
        isTasteTestDraft,
        isTasteTestGeofenced,
        isTasteTestModerated,
        isTasteTestPending,
      },
    },
  } = useTasteTestContext();

  const noticeType = useMemo(() => {
    if (isTasteTestDraft || isTasteTestPending) {
      return "info";
    }

    if (isTasteTestGeofenced || isTasteTestModerated) {
      return "error";
    }

    return "warning";
  }, [isTasteTestDraft, isTasteTestGeofenced, isTasteTestModerated, isTasteTestPending]);

  const noticeTitle = useMemo(() => {
    if (isTasteTestClosing) {
      return "Taste Test closing";
    }

    if (isTasteTestDraft) {
      return "Draft Mode";
    }

    if (isTasteTestGeofenced) {
      return "Geofenced Taste Test";
    }

    if (isTasteTestModerated) {
      return "Moderated Taste Test";
    }

    if (isTasteTestPending) {
      return "Pending Taste Test";
    }

    return "Taste Test closing";
  }, [
    isTasteTestClosing,
    isTasteTestDraft,
    isTasteTestGeofenced,
    isTasteTestModerated,
    isTasteTestPending,
  ]);

  const noticeLabel = useMemo(() => {
    if (isTasteTestClosing) {
      return t("detail.widget.status.Closing");
    }

    if (isTasteTestDraft) {
      return t("detail.widget.status.Draft");
    }

    if (isTasteTestGeofenced) {
      return t("detail.widget.status.Geofenced");
    }

    if (isTasteTestModerated) {
      return t("detail.widget.status.Moderated");
    }

    if (isTasteTestPending) {
      return t("detail.widget.status.Pending");
    }

    if (isTasteTestConcluded) {
      return t("detail.widget.status.Concluded");
    }

    // Default is Closing status once TT is closing
    return t("detail.widget.status.Closing");
  }, [
    isTasteTestClosing,
    isTasteTestDraft,
    isTasteTestGeofenced,
    isTasteTestModerated,
    isTasteTestPending,
    isTasteTestConcluded,
    t,
  ]);

  if (hasContribution && isTasteTestConcluded) {
    return null;
  }

  if (
    isTasteTestInactive ||
    isTasteTestClosing ||
    isTasteTestDraft ||
    isTasteTestGeofenced ||
    isTasteTestModerated ||
    isTasteTestPending ||
    isTasteTestConcluded
  ) {
    return (
      <div
        className={cx(
          "absolute z-50 h-fit w-full max-w-[85%] rounded-lg",
          toolkitCx.layout.background.card,
          toolkitCx.recipes.center.both()
        )}
      >
        <Notice type={noticeType} size="sm" title={noticeTitle}>
          <Text tag="span" size="xs" variant="current">
            {noticeLabel}
          </Text>
        </Notice>
      </div>
    );
  }

  return null;
};

export default MaybeTasteTestContributeWidgetOverlay;
